import React, { useState } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from 'dui'
import copy from 'copy-to-clipboard'
import { Copy } from 'lucide-react'

import { Table, TableHeader, TableBody, TableRow, TableCell } from '@/components/ui/table'
import { Button } from '@/components/ui/button'
import {
  UnansweredQuestion,
  useGetUnansweredQuestionsQuery,
  useResolveUnansweredQuestionMutation,
} from '@/store/chat-api'
import { ConfirmDialog } from '@/components/questions/confirm-dialog'

type Unanswered = UnansweredQuestion

const UnansweredQuestions: React.FC = () => {
  const { data: unanswered = [], isLoading } = useGetUnansweredQuestionsQuery()
  const [handledIds, setHandledIds] = useState<string[]>([])
  const [selected, setSelected] = useState<Unanswered | null>(null)
  const [resolveUnanswered] = useResolveUnansweredQuestionMutation()
  const [confirmId, setConfirmId] = useState<string | null>(null)

  const handleMarkAsHandled = async (id: string): Promise<void> => {
    try {
      await resolveUnanswered(id).unwrap()
      setHandledIds((prev) => [...prev, id])
    } catch (err) {
      console.error('Greška pri označavanju pitanja kao obrađenog:', err)
    }
  }

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-semibold">Neodgovorena pitanja</h2>
      {isLoading && <div>Učitavanje...</div>}
      <Table className="table-fixed">
        <TableHeader>
          <TableRow>
            <TableCell className="w-2/5">Pitanje</TableCell>
            <TableCell className="w-1/5">Email</TableCell>
            <TableCell className="w-1/5">Datum</TableCell>
            <TableCell className="w-1/5">Akcija</TableCell>
          </TableRow>
        </TableHeader>
        <TableBody>
          {unanswered
            .filter((q) => !handledIds.includes(q.id))
            .map((q) => (
              <TableRow key={q.id}>
                <TableCell className="truncate" title={q.question}>
                  {q.question}
                </TableCell>
                <TableCell
                  className="truncate flex items-center justify-between gap-2 min-w-[140px] pr-10"
                  title={q.email ?? ''}
                >
                  {q.email ?? '-'}
                  {q.email && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => copy(q.email ?? '')}
                      title="Kopiraj email"
                      className="text-muted-foreground hover:text-primary"
                    >
                      <Copy className="size-4" />
                    </Button>
                  )}
                </TableCell>
                <TableCell>{new Date(q.time).toLocaleString()}</TableCell>
                <TableCell className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => setSelected(q)}>
                    Detaljnije
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setConfirmId(q.id)}>
                    Obrađeno
                  </Button>
                </TableCell>
              </TableRow>
            ))}
        </TableBody>
      </Table>
      {selected && (
        <Dialog open onOpenChange={(open) => !open && setSelected(null)}>
          <DialogContent
            className="max-h-[60vh] w-[90vw] max-w-md overflow-y-auto"
            aria-describedby={undefined}
          >
            <DialogHeader>
              <DialogTitle>Pitanje</DialogTitle>
            </DialogHeader>
            <div className="max-h-[30vh] overflow-y-auto whitespace-pre-wrap text-sm text-foreground/90 px-1">
              {selected.question}
            </div>
            <DialogFooter>
              <Button onClick={() => setSelected(null)}>Zatvori</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
      {confirmId && (
        <ConfirmDialog
          open={!!confirmId}
          title="Potvrda obrade"
          description="Da li ste sigurni da želite da označite ovo pitanje kao obrađeno?"
          confirmLabel="Potvrdi"
          confirmVariant="default"
          onConfirm={async () => {
            await handleMarkAsHandled(confirmId)
            setConfirmId(null)
          }}
          onCancel={() => setConfirmId(null)}
        />
      )}
    </div>
  )
}

export default UnansweredQuestions
